import inlineSVG from '@gearset/web-core/scripts/inlineSvg.js';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import dracoDecoderJsUrl from 'three/examples/jsm/libs/draco/gltf/draco_decoder.js?url';
import dracoDecoderWasmUrl from 'three/examples/jsm/libs/draco/gltf/draco_decoder.wasm?url';
import dracoWasmWrapperUrl from 'three/examples/jsm/libs/draco/gltf/draco_wasm_wrapper.js?url';
import eachElement from 'Utils/eachElement.js';
import whenVisible from 'Utils/whenVisible.js';
import analytics from 'Scripts/analytics.js';

/* Google's unversioned Draco build ignores the wasm binary DRACOLoader
   injects, so the worker requests a relative wasm URL and the badge never
   loads. These decoders ship with three and match this loader. */
const dracoDecoderFiles = {
	'draco_decoder.js': dracoDecoderJsUrl,
	'draco_decoder.wasm': dracoDecoderWasmUrl,
	'draco_wasm_wrapper.js': dracoWasmWrapperUrl,
};

const revealHero = () => {
	document.querySelector('#hero-content')?.classList.remove('is-invisible');
};

class Badge {
	constructor() {
		/* Loaders */
		const dracoManager = new THREE.LoadingManager();
		dracoManager.setURLModifier(url => {
			const fileName = url.split('/').pop();
			return dracoDecoderFiles[fileName] || url;
		});

		this.dracoLoader = new DRACOLoader(dracoManager);
		/* Rewritten to the bundled decoder files by the loading manager. */
		this.dracoLoader.setDecoderPath('/draco/');

		this.gltfLoader = new GLTFLoader();
		this.gltfLoader.setDRACOLoader(this.dracoLoader);

		/* Container for the badge */
		this.badge = null;

		/* Camera - imported later from GLTF */
		this.camera = null;

		/* Animation mixer - created after importing */
		this.mixer = null;

		/* Renderer */
		this.renderer = {};

		/* Whether the animation is currently running */
		/* The built-in `.isRunning` is not reliable frame-by-frame */
		this.animating = false;

		/* Whether the animation has finished */
		this.finished = false;

		/* Environment map generator */
		this.pmremGenerator = {};

		/* Elapsed time for the camera animation and headline reveal */
		this.timer = new THREE.Timer();
	}

	display() {
		/* Init the scene */
		this.init();
		/* Mouse events & window resize */
		this.handleEvents();
		/* Start loop animation */
		window.requestAnimationFrame(this.render.bind(this));
	}

	init() {
		/* Store the position of the mouse */
		/* Default is center of the screen */
		this.mouse = {
			position: new THREE.Vector2(0, 0.2),
			target: new THREE.Vector2(0, 0.2),
		};

		/* Three.js canvas container and scene */
		this.canvas = document.querySelector('#badge');
		this.scene = new THREE.Scene();

		this.heroContent = document.querySelector('#hero-content');
		this.firstBox = document.querySelector('.box');

		/* Viewport */
		this.calculateViewport();

		/* Renderer */
		this.createRenderer();
	}

	createRenderer() {
		/* Create renderer */
		try {
			this.renderer = new THREE.WebGLRenderer({
				canvas: this.canvas,
				antialias: true,
				alpha: true,
			});
			this.renderer.outputColorSpace = THREE.SRGBColorSpace;
			this.renderer.toneMapping = THREE.CineonToneMapping;
			this.renderer.toneMappingExposure = 1.25;

			this.loadScene();

			/* Display hero text immediately on stacked */
			if (this.ww < 1024) {
				revealHero();
			}
		} catch {
			/* If WebGL context failed, delete the canvas, and fallback to a jpeg */
			this.canvas.remove();

			document.documentElement.classList.add('has-fallback-hero');
			revealHero();
		}
	}

	loadScene() {
		/* Load the scene */
		this.gltfLoader.load('/images/minisites/devops-leaders/3d/leaders.glb', gltf => {
			/* Add imported scene to stage */
			this.scene.add(gltf.scene);

			/* Load and set up environment map */
			new THREE.TextureLoader().load('/images/minisites/devops-leaders/3d/environment.jpg', texture => {
				this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
				this.pmremGenerator.compileEquirectangularShader();

				texture.mapping = THREE.EquirectangularReflectionMapping;
				texture.colorSpace = THREE.SRGBColorSpace;
				this.scene.environment = this.pmremGenerator.fromEquirectangular(texture).texture;
			});

			/* Group badge parts into a single addressable object */
			this.badge = new THREE.Group();
			this.badge.name = 'Badge';
			this.badge.add(this.scene.getObjectByName('Laurel'));
			this.badge.add(this.scene.getObjectByName('Words'));
			this.badge.add(this.scene.getObjectByName('Plate'));
			this.badge.add(this.scene.getObjectByName('Rim'));
			this.scene.add(this.badge);

			/* Use the camera from the imported scene */
			this.camera = gltf.cameras[0];
			this.camera.far = 1000;
			this.camera.fov = 21;
			this.setAspectRatio();
			this.scene.add(this.camera);

			/* Add an ambient light */
			const light = new THREE.AmbientLight(0x404040, 15);
			this.scene.add(light);

			/* Create animation mixer */
			this.mixer = new THREE.AnimationMixer(this.camera);
			this.mixer.addEventListener('finished', () => {
				this.finished = true;
			});

			/* Load camera animation */
			const clip = THREE.AnimationClip.findByName(gltf.animations, 'CameraAction');
			const action = this.mixer.clipAction(clip);
			action.loop = THREE.LoopOnce;
			action.clampWhenFinished = true;

			/* Only begin animating once everything has loaded */
			THREE.DefaultLoadingManager.onLoad = () => {
				/* Play camera animation */
				action.play();
				this.animating = true;

				/* Begin animation ticks */
				this.render();
			};
		}, undefined, () => {
			/* Decoding or fetch failed; don't leave the headline hidden */
			revealHero();
		});
	}

	calculateViewport() {
		/* Calculate viewport and canvas dimensions */
		this.ww = document.documentElement.clientWidth || document.body.clientWidth;
		this.wh = window.innerHeight;
		this.bh = document.querySelector('#badge')?.clientHeight;
		this.ww2 = this.ww * 0.5;
		this.wh2 = this.wh * 0.5;

		this.cw = this.canvas.getBoundingClientRect().width;
		this.ch = this.canvas.getBoundingClientRect().height;
	}

	setAspectRatio() {
		/* Resize can fire before the glTF camera exists */
		if (!this.camera) {
			return;
		}

		/* Update camera; offset badge centre on wide screens */
		if (this.ww >= 1024) {
			this.camera.setViewOffset(this.cw, this.ch, (this.ww * -0.5 / 2), 120, this.cw, this.ch);
			this.camera.zoom = 1;
		} else {
			this.camera.setViewOffset(this.cw, this.ch, 0, 0, this.cw, this.ch);
			this.camera.zoom = this.ww > 480 ? 1.5 : 1.25;
		}

		this.camera.aspect = this.cw / this.ch;
		this.camera.updateProjectionMatrix();

		/* Update renderer */
		if (this.renderer) {
			this.renderer.setSize(this.cw, this.ch);
			this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		}
	}

	handleEvents() {
		/* When user resize window */
		window.addEventListener('resize', this.onResize.bind(this), false);

		/* When user move the mouse */
		document.body.addEventListener(
			'mousemove',
			this.onMouseMove.bind(this),
			false,
		);
	}

	onResize() {
		/* On resize, get new width & height of window */
		this.calculateViewport();
		this.setAspectRatio();
	}

	onMouseMove(event) {
		/* Save mouse X & Y position */
		this.mouse.target.x = (event.clientX - this.ww2) / this.ww2;
		this.mouse.target.y = (this.wh2 - event.clientY) / this.wh2;
	}

	render() {
		/* Calculate time difference between ticks */
		this.timer.update();
		const elapsedTime = this.timer.getElapsed();
		const deltaTime = this.timer.getDelta();

		/* Set up reveals timed to the animation */
		if (elapsedTime > 1 && this.heroContent?.classList.contains('is-invisible')) {
			revealHero();
		}

		if (elapsedTime > 1.6 && !this.firstBox.classList.contains('is-active')) {
			this.firstBox.classList.add('is-active');
		}

		/* Update camera animation frame */
		if (this.mixer) {
			this.mixer.update(deltaTime);
		}

		/* Update the mouse position with some lerp */
		if (this.badge) {
			this.mouse.position.x += (this.mouse.target.x - this.mouse.position.x) / 50;
			this.mouse.position.y += (this.mouse.target.y - this.mouse.position.y) / 50;

			this.badge.rotation.x = this.mouse.position.y * -0.25;
			this.badge.rotation.y = (this.mouse.position.x * 0.25) - 0.125;
		}

		if (this.renderer && this.camera) {
			/* Render frame with above changes */
			this.renderer.render(this.scene, this.camera);

			/* Call tick again on the next frame */
			window.requestAnimationFrame(this.render.bind(this));
		}
	}
}


const offset = element => {
	const box = element.getBoundingClientRect();
	const docElement = document.documentElement;
	return {
		top: box.top + window.pageYOffset - docElement.clientTop,
		left: box.left + window.pageXOffset - docElement.clientLeft,
	};
};

const parallax = () => {
	/* Parallax scrolling effect */
	window.requestAnimationFrame(() => {
		eachElement('.parallax', element => {
			const windowTop = window.pageYOffset;
			const elementTop = offset(element).top;
			const elementHeight = element.getBoundingClientRect().height;
			const viewPortHeight = (window.innerHeight * 0.5) - (elementHeight * 0.5);
			const scrolled = windowTop - elementTop + viewPortHeight;
			const multiplier = element.getAttribute('data-depth') * (element.classList.contains('is-reverse') ? -1 : 1);
			const amount = scrolled * multiplier;

			element.style.transform = `translate3d(0,${amount}px, 0)`;
		});
	});
};

document.addEventListener('DOMContentLoaded', () => {
	/* Load SVGs */
	inlineSVG.init({
		svgSelector: 'img.svg',
		initClass: 'js-inlinesvg',
	}, () => {
		document.documentElement.classList.toggle('is-loaded');
	});

	if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
		/* Parallax for leader images */
		window.addEventListener('scroll', parallax);
		window.addEventListener('resize', parallax);
		window.addEventListener('load', parallax);

		/* 3D Badge */
		const badge = new Badge();
		badge.display();

		/* When visible */
		whenVisible();
	} else {
		/* Show all the things that have been hidden for animating */
		document.documentElement.classList.add('has-fallback-hero');
		revealHero();

		eachElement('.box', element => {
			element.classList.add('is-active', 'is-static');
		});
	}

	analytics.trackTaggedAnchors();
});
